import { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Palette, Upload, Save, Image as ImageIcon, Shield, Tag, ChevronRight, ChevronLeft, LayoutTemplate, Eye, Newspaper, Plus, Trash2, Share2, ArrowUp, ArrowDown, Globe, Activity, Send, Flag, Database, Ban, LayoutList, Clock, Quote, ShieldCheck, Megaphone, Crown, Inbox, Heart } from "lucide-react";
import { toast } from "sonner";
import CategoryManager from "../components/CategoryManager";
import LandingHero from "../components/LandingHero";
import EmailSettingsCard from "../components/EmailSettingsCard";
import BillingSettingsCard from "../components/BillingSettingsCard";
import FeatureFlagsCard from "../components/FeatureFlagsCard";
import TestimonialsCard from "../components/TestimonialsCard";
import SiteTimezoneCard from "../components/SiteTimezoneCard";
import MaintenanceCard from "../components/MaintenanceCard";
import SampleDataCard from "../components/SampleDataCard";
import SystemHealthCard from "../components/SystemHealthCard";
import PublicProfilesCard from "../components/PublicProfilesCard";
import AdminReportsCard from "../components/AdminReportsCard";
import InstallPreviewCard from "../components/InstallPreviewCard";
import BanAuditLogCard from "../components/BanAuditLogCard";
import AdminCoachesCard from "../components/AdminCoachesCard";
import AdminUserActivityCard from "../components/AdminUserActivityCard";
import AdminAnnouncementsCard from "../components/AdminAnnouncementsCard";
import AdminMembershipsCard from "../components/AdminMembershipsCard";
import PageHero from "../components/PageHero";
import ProductTour from "../components/ProductTour";
import { Link } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { SOCIAL_PLATFORMS, getPlatform } from "../components/socialPlatforms";

const ADMIN_TOUR_STEPS = [
  {
    placement: "center",
    title: "Welcome to the Admin Panel",
    body: "90-second tour of the tile grid. Use ← → to navigate, Esc to exit anytime.",
  },
  {
    selector: '[data-testid="admin-tile-health"]',
    placement: "bottom",
    title: "System Health",
    body: "Backend probes, Mongo ping, Resend status, scheduler heartbeats, and the latest error log tail — check this first when anything feels off.",
  },
  {
    selector: '[data-testid="admin-tile-user-activity"]',
    placement: "bottom",
    title: "User Activity",
    body: "Every user with last-login timestamp + recent actions (shots, weights, meals, community, coach shares). Perfect for support triage.",
  },
  {
    selector: '[data-testid="admin-tile-coaches"]',
    placement: "bottom",
    title: "Coach Roster",
    body: "Add, message, feature (pin), or remove any coach. Broadcast to all coaches at once. Only visible when the Coaching feature flag is on.",
  },
  {
    selector: '[data-testid="admin-tile-announcements"]',
    placement: "bottom",
    title: "Announcements",
    body: "Publish a site-wide banner every user sees. Supports **bold**, [links](https://url), scheduled publishing, and seen/dismissed analytics.",
  },
  {
    selector: '[data-testid="admin-tile-feature-flags"]',
    placement: "top",
    title: "Feature Flags",
    body: "The master control panel — 15 toggles for every major section. Turn a flag off and the feature disappears everywhere on the site.",
  },
  {
    selector: '[data-testid="admin-tile-branding"]',
    placement: "top",
    title: "Site Branding",
    body: "Site name, tagline, logo, favicon, and primary accent color. Live preview updates as you edit.",
  },
  {
    selector: '[data-testid="admin-tile-email"]',
    placement: "top",
    title: "Email Delivery",
    body: "Resend + SMTP config. Send a test to yourself before the first real reminder fires.",
  },
  {
    placement: "center",
    title: "Deep dive?",
    body: 'Every tile is documented step-by-step in Tutorial → "For admins" tab. Retake this tour anytime from the button at the top of the panel.',
  },
];

export default function AdminSettings() {
  const { settings, refreshSettings } = useSettings();
  const [siteName, setSiteName] = useState("");
  const [siteDomain, setSiteDomain] = useState("");
  const [tagline, setTagline] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#0D9488");
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openCategoryManager, setOpenCategoryManager] = useState(false);
  const [categoryCount, setCategoryCount] = useState(null);
  // Landing page editable fields
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [newsItems, setNewsItems] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [landing, setLanding] = useState({
    landing_intro_label: "",
    landing_headline: "",
    landing_headline_highlight: "",
    landing_headline_suffix: "",
    landing_body: "",
    landing_stat_1_value: "",
    landing_stat_1_label: "",
    landing_stat_2_value: "",
    landing_stat_2_label: "",
    landing_stat_3_value: "",
    landing_stat_3_label: "",
    landing_form_title: "",
    landing_form_subtitle: "",
  });

  const loadCategoryCount = () => {
    api.get("/community/categories")
      .then((r) => setCategoryCount(r.data.length))
      .catch(() => setCategoryCount(null));
  };
  useEffect(() => { loadCategoryCount(); }, []);

  useEffect(() => {
    setSiteName(settings.site_name || "");
    setSiteDomain(settings.site_domain || "");
    setTagline(settings.tagline || "");
    setPrimaryColor(settings.primary_color || "#0D9488");
    setLogoUrl(settings.logo_url || null);
    setFaviconUrl(settings.favicon_url || null);
    setLanding({
      landing_intro_label: settings.landing_intro_label || "",
      landing_headline: settings.landing_headline || "",
      landing_headline_highlight: settings.landing_headline_highlight || "",
      landing_headline_suffix: settings.landing_headline_suffix || "",
      landing_body: settings.landing_body || "",
      landing_stat_1_value: settings.landing_stat_1_value || "",
      landing_stat_1_label: settings.landing_stat_1_label || "",
      landing_stat_2_value: settings.landing_stat_2_value || "",
      landing_stat_2_label: settings.landing_stat_2_label || "",
      landing_stat_3_value: settings.landing_stat_3_value || "",
      landing_stat_3_label: settings.landing_stat_3_label || "",
      landing_form_title: settings.landing_form_title || "",
      landing_form_subtitle: settings.landing_form_subtitle || "",
    });
    setHeroImageUrl(settings.hero_image_url || null);
    setNewsItems(Array.isArray(settings.news_items) ? settings.news_items : []);
    setSocialLinks(Array.isArray(settings.social_links) ? settings.social_links : []);
  }, [settings]);

  // Live preview payload combines the current form state
  const previewSettings = useMemo(() => ({
    ...settings,
    site_name: siteName || settings.site_name,
    tagline: tagline || settings.tagline,
    primary_color: primaryColor,
    logo_url: logoUrl,
    favicon_url: faviconUrl,
    hero_image_url: heroImageUrl,
    news_items: newsItems,
    social_links: socialLinks,
    ...landing,
  }), [settings, siteName, tagline, primaryColor, logoUrl, faviconUrl, heroImageUrl, newsItems, socialLinks, landing]);

  const setL = (k, v) => setLanding((cur) => ({ ...cur, [k]: v }));

  const onHeroUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Hero image must be under 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setHeroImageUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const addNewsItem = () => setNewsItems((cur) => [...cur, { date: new Date().toISOString().slice(0, 10), title: "", body: "" }]);
  const removeNewsItem = (idx) => setNewsItems((cur) => cur.filter((_, i) => i !== idx));
  const updateNewsItem = (idx, k, v) => setNewsItems((cur) => cur.map((n, i) => i === idx ? { ...n, [k]: v } : n));

  // Social links helpers
  const addSocialLink = () => {
    if (socialLinks.length >= 12) { toast.error("Max 12 social links"); return; }
    setSocialLinks((cur) => [...cur, { platform: "facebook", url: "" }]);
  };
  const removeSocialLink = (idx) => setSocialLinks((cur) => cur.filter((_, i) => i !== idx));
  const updateSocialLink = (idx, k, v) => setSocialLinks((cur) => cur.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const moveSocialLink = (idx, dir) => setSocialLinks((cur) => {
    const next = [...cur];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return cur;
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error("Logo must be under 512 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleFaviconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 256 * 1024) {
      toast.error("Favicon must be under 256 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setFaviconUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/settings", {
        site_name: siteName.trim() || "The Amino Exchange",
        site_domain: siteDomain.trim() || null,
        tagline: tagline.trim() || null,
        primary_color: primaryColor,
        logo_url: logoUrl,
        favicon_url: faviconUrl,
        hero_image_url: heroImageUrl,
        news_items: newsItems.filter((n) => (n.title || "").trim() || (n.body || "").trim()),
        social_links: socialLinks
          .map((l) => ({ platform: (l.platform || "").toLowerCase(), url: (l.url || "").trim() }))
          .filter((l) => l.platform && l.url),
        ...landing,
      });
      await refreshSettings();
      toast.success("Site settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const [section, setSection] = useState(() => (typeof window !== "undefined" && window.location.hash.slice(1)) || null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const next = section ? `#${section}` : "";
    if (window.location.hash !== next) window.history.replaceState(null, "", window.location.pathname + window.location.search + next);
  }, [section]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHash = () => setSection(window.location.hash.slice(1) || null);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const TILES = [
    { key: "announcements",    title: "Announcements",         desc: "Publish a site-wide banner every user sees on login",       icon: Megaphone,    flag: "announcements" },
    { key: "ban-audit",        title: "Ban Audit Log",         desc: "History of every ban / unban with reason",    icon: Ban },
    { key: "billing",          title: "Billing & Payments",    desc: "PayPal client id + premium settings",         icon: Database },
    { key: "coaches",          title: "Coach Roster",          desc: "Add · message · remove coaches; per-coach client counts",  icon: ShieldCheck,  flag: "coaching" },
    { key: "categories",       title: "Discussion Categories", desc: "Community board sections",                    icon: Tag },
    { key: "email",            title: "Email Delivery",        desc: "Resend + SMTP configuration",                 icon: Send },
    { key: "feature-flags",    title: "Feature Flags",         desc: "On/off switches for Community, Playbook, Nutrition, Cycles, and every other section",     icon: LayoutList },
    { key: "feedback",         title: "Feedback Inbox",        desc: "Private user feedback — Report a Problem, Share an Idea",   icon: Inbox, external: "/admin/feedback" },
    { key: "install-preview",  title: "Install Preview",       desc: "Live iOS + Android home-screen mockups",      icon: ImageIcon },
    { key: "landing",          title: "Landing Page",          desc: "Marketing copy, hero image, and CTAs",        icon: Newspaper },
    { key: "maintenance",      title: "Maintenance Mode",      desc: "Take the site offline gracefully",            icon: LayoutTemplate },
    { key: "memberships",      title: "Membership Management", desc: "Grant / revoke Pro access + revenue events log",             icon: Crown,        flag: "memberships" },
    { key: "reports",          title: "Moderation Queue",      desc: "Review flagged users, topics & replies",      icon: Flag },
    { key: "public-profiles",  title: "Public Profiles",       desc: "Allow anonymous /u/:id views",                icon: Globe },
    { key: "sample-data",      title: "Sample Data",           desc: "Toggle demo shots + vials for screenshots",   icon: Eye },
    { key: "branding",         title: "Site Branding",         desc: "Name, tagline, logo, colors, favicon",        icon: Palette },
    { key: "site-timezone",    title: "Site Timezone",         desc: "Canonical time zone every date renders in",   icon: Clock },
    { key: "support-page",     title: "Support Page",          desc: "Edit every visible line on the Support Us page — draft, preview, publish", icon: Heart, external: "/admin/support-page" },
    { key: "health",           title: "System Health",         desc: "Backend probes + error log tail",             icon: Activity },
    { key: "testimonials",     title: "Testimonials",          desc: "Rotating user quotes on the landing page",    icon: Quote },
    { key: "user-activity",    title: "User Activity",         desc: "Last login + recent actions per user for support triage",  icon: Activity },
  ];
  const flagsMap = settings?.feature_flags || {};
  // Tiles gated by feature flag: filter out when the flag is explicitly OFF.
  const visibleTiles = TILES.filter((t) => !t.flag || flagsMap[t.flag] !== false);
  const activeTile = visibleTiles.find((t) => t.key === section);
  const show = (k) => section === k;
  const [tourOpen, setTourOpen] = useState(false);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Admin Console"
        title={
          <span className="flex items-center gap-4 flex-wrap">
            <span data-testid="admin-hub-title">{activeTile ? activeTile.title : "Site Settings"}</span>
            {activeTile && (
              <button type="button" onClick={() => setSection(null)}
                className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-800 px-3 h-9 text-[11px] uppercase tracking-widest font-mono-num transition-colors"
                data-testid="admin-back-to-hub">
                <ChevronLeft className="w-3.5 h-3.5" /> All settings
              </button>
            )}
          </span>
        }
        subtitle={activeTile ? activeTile.desc : "Every knob and dial. Pick a tile to jump in."}
        icon={<Shield className="w-8 h-8 text-[color:var(--brand,#0D9488)]" />}
        gradient
      />

      {/* Admin tour + Tutorial links — only on the tile grid */}
      {!section && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="admin-tour-strip">
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800 text-[12px] font-semibold transition-colors"
            data-testid="admin-tour-open"
          >
            <GraduationCap className="w-3.5 h-3.5" /> Take the admin tour
          </button>
          <Link
            to="/tutorial"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-[12px] font-semibold transition-colors"
            data-testid="admin-tour-tutorial-link"
          >
            Full admin tutorial <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {!section && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-tiles-grid">
          {visibleTiles.map((t) => (
            <button key={t.key} type="button"
              onClick={() => t.external ? (window.location.href = t.external) : setSection(t.key)}
              data-testid={`admin-tile-${t.key}`}
              className="text-left border border-slate-200 bg-white hover:border-[#0D9488] hover:bg-slate-50/40 transition-colors p-5 flex flex-col gap-3 group">
              <div className="w-10 h-10 flex items-center justify-center border border-slate-200 group-hover:border-[#0D9488]/60 text-[#0D9488]">
                <t.icon className="w-5 h-5" />
              </div>
              <div className="font-display font-black uppercase tracking-tight text-lg text-slate-900">{t.title}</div>
              <div className="text-xs text-slate-500 leading-relaxed">{t.desc}</div>
              <div className="mt-auto flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-slate-400 group-hover:text-[#0D9488]">
                Open <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Maintenance mode — pinned at the top so the toggle is one click away */}
      {show("health") && (
      <div className="mb-6">
        <SystemHealthCard />
      </div>
      )}
      {show("maintenance") && (
      <div className="mb-6">
        <MaintenanceCard />
      </div>
      )}

      {/* Sample-data toggle for demos + screenshots */}
      {show("sample-data") && (
      <div className="mb-6">
        <SampleDataCard />
      </div>
      )}

      {/* Shareable public profile pages */}
      {show("public-profiles") && (
      <div className="mb-6">
        <PublicProfilesCard />
      </div>
      )}

      {/* Moderation queue — user reports */}
      {show("reports") && (
      <div className="mb-6">
        <AdminReportsCard />
      </div>
      )}

      {/* Ban audit log — every ban/unban with actor + reason */}
      {show("ban-audit") && (
      <div className="mb-6">
        <BanAuditLogCard />
      </div>
      )}

      {/* Coach roster — add / message / remove */}
      {show("coaches") && (flagsMap.coaching !== false) && (
      <div className="mb-6">
        <AdminCoachesCard />
      </div>
      )}

      {show("user-activity") && (
      <div className="mb-6">
        <AdminUserActivityCard />
      </div>
      )}

      {show("announcements") && (flagsMap.announcements !== false) && (
      <div className="mb-6">
        <AdminAnnouncementsCard />
      </div>
      )}

      {show("memberships") && (flagsMap.memberships !== false) && (
      <div className="mb-6">
        <AdminMembershipsCard />
      </div>
      )}

      {show("branding") && (
      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="site-settings-form">
        {/* Identity */}
        <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-none p-6">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">// Identity</div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Site Name</Label>
              <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} required
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-display uppercase tracking-tight text-lg" data-testid="settings-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Tagline (subtitle)</Label>
              <Input value={tagline} onChange={(e) => setTagline(e.target.value)}
                placeholder="e.g., Precision Dose Protocol"
                className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="settings-tagline" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Site Domain</Label>
              <Input
                value={siteDomain}
                onChange={(e) => setSiteDomain(e.target.value)}
                placeholder="aminotrade.com  or  https://app.aminotrade.com"
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num"
                data-testid="settings-site-domain"
              />
              <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest leading-relaxed">
                Used as the base URL in outbound emails (password reset links + community reply notifications).
                Once your DNS points at this app, enter the domain here and every email link will use it automatically.
                Leave blank to keep using the preview URL. Paste with or without <span className="text-slate-800">https://</span>.
              </div>
              {siteDomain && (
                <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                  Password reset link preview:{" "}
                  <span className="text-[color:var(--brand,#0D9488)] normal-case">
                    {(() => {
                      let d = siteDomain.trim();
                      if (!/^https?:\/\//i.test(d)) d = `https://${d}`;
                      return `${d.replace(/\/$/, "")}/reset-password?token=…`;
                    })()}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Logo
              </Label>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" />
                  ) : (
                    <span className="text-slate-400 text-[10px] uppercase tracking-widest">None</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 border border-slate-200 hover:border-[color:var(--brand,#0D9488)] bg-slate-50 hover:bg-slate-50 px-4 h-10 cursor-pointer transition-colors text-sm text-slate-800" data-testid="settings-logo-upload-label">
                    <Upload className="w-4 h-4" /> Upload Logo (PNG/JPG, ≤ 512 KB)
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="settings-logo-input" />
                  </label>
                  {logoUrl && (
                    <Button type="button" variant="outline" onClick={() => setLogoUrl(null)}
                      className="h-8 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 text-xs" data-testid="settings-logo-remove">
                      Remove logo
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Favicon
              </Label>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="favicon" className="w-8 h-8 object-contain" data-testid="settings-favicon-preview" />
                  ) : (
                    <span className="text-slate-400 text-[10px] uppercase tracking-widest">None</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 border border-slate-200 hover:border-[color:var(--brand,#0D9488)] bg-slate-50 hover:bg-slate-50 px-4 h-10 cursor-pointer transition-colors text-sm text-slate-800" data-testid="settings-favicon-upload-label">
                    <Upload className="w-4 h-4" /> Upload Favicon (PNG/SVG/ICO, ≤ 256 KB)
                    <input type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg,image/webp" onChange={handleFaviconUpload} className="hidden" data-testid="settings-favicon-input" />
                  </label>
                  <p className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest leading-relaxed">
                    Recommended: 512×512 PNG or square SVG. Browsers cache favicons aggressively — hard-refresh to see updates.
                  </p>
                  {faviconUrl && (
                    <Button type="button" variant="outline" onClick={() => setFaviconUrl(null)}
                      className="h-8 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 text-xs" data-testid="settings-favicon-remove">
                      Remove favicon
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Color */}
        <Card className="bg-white border border-slate-200 rounded-none p-6">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4 flex items-center gap-1.5">
            <Palette className="w-3 h-3" /> // Brand Color
          </div>
          <div className="space-y-4">
            <div className="w-full h-24 border border-slate-200" style={{ backgroundColor: primaryColor }} data-testid="settings-color-preview" />
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Hex Color</Label>
              <div className="flex gap-2">
                <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-11 w-14 bg-slate-50 border border-slate-200 rounded-none cursor-pointer" data-testid="settings-color-picker" />
                <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)}
                  pattern="#[0-9a-fA-F]{6}"
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num uppercase" data-testid="settings-color-hex" />
              </div>
            </div>
            <div className="grid grid-cols-6 gap-1.5">
              {["#0D9488", "#EF4444", "#22C55E", "#3B82F6", "#A855F7", "#EC4899"].map((c) => (
                <button key={c} type="button" onClick={() => setPrimaryColor(c)}
                  className="h-8 border border-slate-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} data-testid={`settings-color-preset-${c.slice(1)}`} />
              ))}
            </div>
          </div>
        </Card>

        <div className="lg:col-span-3 flex justify-end">
          <Button type="submit" disabled={saving}
            className="h-12 px-8 rounded-none text-black uppercase tracking-widest font-semibold"
            style={{ backgroundColor: primaryColor }}
            data-testid="settings-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </form>
      )}

      {/* Install Preview — live iOS + Android mockups fed by the form state above */}
      {show("install-preview") && (
      <div className="mt-6">
        <InstallPreviewCard
          siteName={siteName}
          tagline={tagline}
          faviconUrl={faviconUrl}
          primaryColor={primaryColor}
        />
      </div>
      )}

      {/* Billing / PayPal */}
      {show("billing") && (
      <div className="mt-6">
        <BillingSettingsCard />
      </div>
      )}

      {/* Playbook Management — feature flags */}
      {show("feature-flags") && (
      <div className="mt-6">
        <FeatureFlagsCard />
      </div>
      )}

      {/* Testimonials — rotating user quotes on the landing page */}
      {show("testimonials") && (
      <div className="mt-6">
        <TestimonialsCard />
      </div>
      )}

      {/* Site Timezone — canonical time zone for every date on the app */}
      {show("site-timezone") && (
      <div className="mt-6">
        <SiteTimezoneCard />
      </div>
      )}

      {/* Discussion Categories — mounted under Site Settings for admins */}
      {show("categories") && (
      <div className="mt-6">
        <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="admin-settings-categories-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[#0D9488] flex items-center justify-center">
              <Tag className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Community</div>
              <div className="font-display font-black uppercase tracking-tight text-xl">Discussion Categories</div>
            </div>
          </div>
          <p className="text-slate-600 text-sm mb-4 max-w-2xl">
            Create, rename, recolor, or remove the categories users pick from when posting. Deleting a category will move any posts using it back to "Uncategorized" (posts are never lost).
          </p>
          <div className="flex items-center justify-between border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-3">
              <Tag className="w-4 h-4 text-[#0D9488]" />
              <div>
                <div className="font-mono-num text-[11px] uppercase tracking-widest text-slate-500">Total categories</div>
                <div className="font-display font-black text-2xl text-slate-900 tabular-nums" data-testid="admin-settings-categories-count">
                  {categoryCount == null ? "—" : categoryCount}
                </div>
              </div>
            </div>
            <Button
              type="button"
              onClick={() => setOpenCategoryManager(true)}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-10 px-5 uppercase tracking-widest text-xs font-semibold"
              data-testid="admin-settings-manage-categories"
            >
              <Tag className="w-3.5 h-3.5 mr-2" /> Manage Categories <ChevronRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </div>
        </Card>
      </div>
      )}

      <CategoryManager open={openCategoryManager} onOpenChange={setOpenCategoryManager} onChanged={loadCategoryCount} />

      {/* ============ Email Delivery Settings ============ */}
      {show("email") && (
      <div className="mt-6">
        <EmailSettingsCard primaryColor={primaryColor} />
      </div>
      )}

      {/* ============ Landing Page Editor ============ */}
      {show("landing") && (
      <div className="mt-6">
        <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="landing-editor-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: primaryColor }}>
              <LayoutTemplate className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Public</div>
              <div className="font-display font-black uppercase tracking-tight text-xl">Landing Page</div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs font-mono-num uppercase tracking-widest text-slate-500">
              <Eye className="w-3 h-3" /> Live preview updates as you type
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form column */}
            <form onSubmit={onSubmit} className="space-y-4" data-testid="landing-editor-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Intro Label</Label>
                <Input value={landing.landing_intro_label} onChange={(e) => setL("landing_intro_label", e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num" data-testid="landing-intro-label-input" placeholder="// v1.0 — Web Terminal" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Headline</Label>
                  <Input value={landing.landing_headline} onChange={(e) => setL("landing_headline", e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="landing-headline-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Highlight</Label>
                  <Input value={landing.landing_headline_highlight} onChange={(e) => setL("landing_headline_highlight", e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="landing-highlight-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Suffix</Label>
                  <Input value={landing.landing_headline_suffix} onChange={(e) => setL("landing_headline_suffix", e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="landing-suffix-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Body Paragraph</Label>
                <Textarea rows={3} value={landing.landing_body} onChange={(e) => setL("landing_body", e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="landing-body-input" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Stat Rail (value + label)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="space-y-1">
                      <Input value={landing[`landing_stat_${n}_value`]} onChange={(e) => setL(`landing_stat_${n}_value`, e.target.value)}
                        placeholder="value" className="bg-slate-50 border-slate-200 rounded-none h-9 font-display font-black text-sm" data-testid={`landing-stat-${n}-value`} />
                      <Input value={landing[`landing_stat_${n}_label`]} onChange={(e) => setL(`landing_stat_${n}_label`, e.target.value)}
                        placeholder="label" className="bg-slate-50 border-slate-200 rounded-none h-9 text-xs" data-testid={`landing-stat-${n}-label`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Form Title</Label>
                  <Input value={landing.landing_form_title} onChange={(e) => setL("landing_form_title", e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="landing-form-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Form Subtitle</Label>
                  <Input value={landing.landing_form_subtitle} onChange={(e) => setL("landing_form_subtitle", e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="landing-form-subtitle-input" />
                </div>
              </div>

              {/* Hero image uploader */}
              <div className="space-y-1.5 pt-2 border-t border-slate-200">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Hero Background Image</Label>
                <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 p-2">
                  {heroImageUrl ? (
                    <img src={heroImageUrl} alt="hero" className="w-24 h-16 object-cover border border-slate-200" data-testid="landing-hero-image-preview" />
                  ) : (
                    <div className="w-24 h-16 border border-slate-200 flex items-center justify-center text-slate-400">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                  )}
                  <label className="flex-1 cursor-pointer border border-dashed border-slate-300 hover:border-[#0D9488]/50 text-slate-600 hover:text-[#0D9488] px-3 py-2 text-xs font-mono-num uppercase tracking-widest text-center transition-colors">
                    <Upload className="w-3 h-3 inline mr-1" /> Upload hero (≤ 1 MB)
                    <input type="file" accept="image/*" onChange={onHeroUpload} className="hidden" data-testid="landing-hero-image-input" />
                  </label>
                  {heroImageUrl && (
                    <Button type="button" variant="ghost" onClick={() => setHeroImageUrl(null)}
                      className="rounded-none text-slate-500 hover:text-[#FF3B30] h-9" data-testid="landing-hero-image-clear">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                  Overrides the default stock geometric background. Renders at 40% opacity behind the hero text.
                </div>
              </div>

              {/* News editor */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                    <Newspaper className="w-3 h-3" /> News Items ({newsItems.length})
                  </Label>
                  <Button type="button" onClick={addNewsItem}
                    className="h-8 rounded-none bg-slate-50 hover:bg-slate-200 text-slate-900 uppercase tracking-widest text-[10px] font-semibold px-3"
                    data-testid="landing-news-add">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                {newsItems.length === 0 ? (
                  <div className="border border-dashed border-slate-200 p-4 text-center text-xs font-mono-num text-slate-500 uppercase tracking-widest">
                    No news items yet. Add one to broadcast an update on the front page.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {newsItems.map((n, i) => (
                      <div key={i} className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`landing-news-row-${i}`}>
                        <div className="grid grid-cols-[110px_1fr_36px] gap-2 items-start">
                          <Input type="date" value={n.date || ""} onChange={(e) => updateNewsItem(i, "date", e.target.value)}
                            className="bg-slate-50 border-slate-200 rounded-none h-9 font-mono-num text-xs" data-testid={`landing-news-date-${i}`} />
                          <Input value={n.title || ""} onChange={(e) => updateNewsItem(i, "title", e.target.value)}
                            placeholder="Headline" maxLength={140}
                            className="bg-slate-50 border-slate-200 rounded-none h-9" data-testid={`landing-news-title-${i}`} />
                          <Button type="button" variant="ghost" onClick={() => removeNewsItem(i)}
                            className="rounded-none text-slate-500 hover:text-[#FF3B30] h-9 w-9 p-0"
                            data-testid={`landing-news-remove-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <Textarea rows={2} value={n.body || ""} onChange={(e) => updateNewsItem(i, "body", e.target.value)}
                          placeholder="Details (optional)" maxLength={600}
                          className="bg-slate-50 border-slate-200 rounded-none resize-none text-sm"
                          data-testid={`landing-news-body-${i}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Social links editor */}
              <div className="space-y-2 pt-2 border-t border-slate-200" data-testid="landing-social-editor">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                    <Share2 className="w-3 h-3" /> Social Links ({socialLinks.length})
                  </Label>
                  <Button type="button" onClick={addSocialLink}
                    className="h-8 rounded-none bg-slate-50 hover:bg-slate-200 text-slate-900 uppercase tracking-widest text-[10px] font-semibold px-3"
                    data-testid="landing-social-add">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                {socialLinks.length === 0 ? (
                  <div className="border border-dashed border-slate-200 p-4 text-center text-xs font-mono-num text-slate-500 uppercase tracking-widest">
                    No social links yet. Add one to show icons on the front page.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {socialLinks.map((l, i) => {
                      const p = getPlatform(l.platform);
                      const IconPreview = p.icon;
                      return (
                        <div key={i} className="border border-slate-200 bg-slate-50 p-2 flex items-center gap-2" data-testid={`landing-social-row-${i}`}>
                          <div className="w-9 h-9 flex items-center justify-center border border-slate-200 bg-black text-slate-800 shrink-0">
                            <IconPreview className="w-4 h-4" />
                          </div>
                          <select
                            value={l.platform || "facebook"}
                            onChange={(e) => updateSocialLink(i, "platform", e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-none h-9 text-xs px-2 font-mono-num uppercase tracking-widest w-40"
                            data-testid={`landing-social-platform-${i}`}
                          >
                            {SOCIAL_PLATFORMS.map((sp) => (
                              <option key={sp.id} value={sp.id}>{sp.label}</option>
                            ))}
                          </select>
                          <Input
                            value={l.url || ""}
                            onChange={(e) => updateSocialLink(i, "url", e.target.value)}
                            placeholder="https://facebook.com/yourpage"
                            className="bg-slate-50 border-slate-200 rounded-none h-9 flex-1 font-mono-num text-xs"
                            data-testid={`landing-social-url-${i}`}
                          />
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button type="button" variant="ghost" onClick={() => moveSocialLink(i, -1)} disabled={i === 0}
                              className="rounded-none text-slate-500 hover:text-[#0D9488] h-9 w-8 p-0 disabled:opacity-30"
                              data-testid={`landing-social-up-${i}`} title="Move up">
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button type="button" variant="ghost" onClick={() => moveSocialLink(i, 1)} disabled={i === socialLinks.length - 1}
                              className="rounded-none text-slate-500 hover:text-[#0D9488] h-9 w-8 p-0 disabled:opacity-30"
                              data-testid={`landing-social-down-${i}`} title="Move down">
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                            <Button type="button" variant="ghost" onClick={() => removeSocialLink(i)}
                              className="rounded-none text-slate-500 hover:text-[#FF3B30] h-9 w-8 p-0"
                              data-testid={`landing-social-remove-${i}`} title="Remove">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                  Icons appear under the sign-in form on the front page. Facebook, Instagram, YouTube, X, TikTok, Skool, Discord and more available.
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <Button type="submit" disabled={saving}
                  className="h-11 px-6 rounded-none text-black uppercase tracking-widest font-semibold"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="landing-save">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Landing Page"}
                </Button>
              </div>
            </form>

            {/* Live preview column */}
            <div className="space-y-2" data-testid="landing-preview-column">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Live preview (visitors will see)</div>
              <div className="border border-slate-200 overflow-hidden bg-black" data-testid="landing-preview-frame">
                <div className="origin-top-left scale-[0.55] w-[181%] h-[350px] pointer-events-none select-none">
                  <LandingHero settings={previewSettings} previewMode>
                    <div className="mt-6 space-y-3">
                      <div className="h-10 bg-slate-50 border border-slate-200" />
                      <div className="h-10 bg-slate-50 border border-slate-200" />
                      <div className="h-10 flex items-center justify-center text-black font-mono-num uppercase tracking-widest text-xs font-bold" style={{ background: primaryColor }}>
                        {landing.landing_form_title || "Sign in"} →
                      </div>
                    </div>
                  </LandingHero>
                </div>
              </div>
              <div className="text-[10px] font-mono-num text-slate-400 uppercase tracking-widest">
                Preview is scaled 55%. Actual page is full width.
              </div>
            </div>
          </div>
        </Card>
      </div>
      )}

      <ProductTour steps={ADMIN_TOUR_STEPS} open={tourOpen} onClose={() => setTourOpen(false)} />
    </AppLayout>
  );
}
